import React, { useCallback, useMemo, useState } from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { bidding } from '@adc/blueprint'

import useBreakpoints from '../../../../hooks/useBreakpoints'
import type {
  Listing,
  OnlineAuctionSegmentBidRank,
  OnlineAuctionSegmentType,
  Party,
  Person,
  CurrentTargetAudience,
  OnlineAuctionSegment,
  ClosingMethod,
  User,
} from '@adc/sdk/types'
// @ts-ignore no types
import {
  Loader,
  Money,
  Text,
  EmptyState,
  InfoPill,
  NullComponent,
} from '../../../elementals'
import PopupTooltip from '../../../overlays/popupTooltip'
// @ts-ignore no types
import { RankList, ExpandableCard } from '../../../layouts'
import styles from './styles.scss'
import BidRankTerms from './bidRankTerms'
import BidRankDetails from './bidRankDetails'
import {
  getInitials,
  getRankAndOrdinal,
  acceptedOffer,
  isTied,
  NET_OFFER_AMOUNT_TOOLTIP,
} from './utils'
import { SlottableComponent } from '../../../../types'
import {
  formatDate,
  formatEnum,
  formatMoney,
} from '../../../../utils/formatters'
// @ts-ignore no types
import smartSaleWhiteLogo from '../../../../../src/images/smartsale-white-logo.svg'
import { Image } from '../../../../../src'
import Icon from '../../../elementals/icon'
import InitialsAvatar from '../../../elementals/initialsAvatar'
import LabelValue from '../../../elementals/labelValue'
import { ColorNames } from '../../../../constants'
import BidRankRibbon from './bidRankRibbon'
import { match } from 'ts-pattern'
import BidRankActionPill from './bidRankActionPill'
import BidRankColumnHeader from './bidRankColumnHeader'

const cx = classnames.bind(styles)
export interface BidRankListProps extends SlottableComponent<'actions'> {
  rankList: OnlineAuctionSegmentBidRank[]
  segmentType: OnlineAuctionSegmentType
  listing?: Listing
  isLoading?: boolean
  viewingFrom?: CurrentTargetAudience
  segment?: OnlineAuctionSegment
  closingMethod?: ClosingMethod
  embeddedContext?: string
  actionPill?: {
    action: string
    duration?: number
    onTimeout?: () => void
  }
}

const BidRankList = ({
  listing,
  rankList,
  segmentType,
  isLoading,
  slots,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  segment,
  actionPill,
  embeddedContext,
}: BidRankListProps) => {
  const [expanded, setExpanded] = useState<string | null>(null)
  const breakpoint = useBreakpoints()
  const isMobile = breakpoint === 'mobile'
  const requestRentBack = useMemo(
    () =>
      listing?.seller_terms?.leaseback_terms?.leaseback_period_in_days !=
        null &&
      listing?.seller_terms?.leaseback_terms?.leaseback_period_in_days > 0,
    [listing]
  )
  const isEnglishType = useMemo(
    () => segmentType === graph.enums.OnlineAuctionSegmentType.English,
    [segmentType]
  )
  const headerStyle = useMemo(
    () =>
      segment && bidding.utils.onlineAuctionUtils.isHighestAndBestRound(segment)
        ? 'header-purple'
        : 'header',
    [segment]
  )

  const handleExpandOrCollapse = useCallback(
    (bidId: string) => {
      setExpanded(bidId === expanded ? null : bidId)
    },
    [expanded]
  )

  const goToUserManagementEditPage = (user: User) => {
    const url = `/user-management/${user?.user_id ?? 0}/edit`
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  const userAvatar = (bidRank: OnlineAuctionSegmentBidRank) => {
    const party = bidRank?.party as Party
    const person = party?.person as Person
    const user = bidRank?.bid?.current_highest_bidder_user as User
    const initialsAvatar = (
      <Text
        color='white'
        size='huge'
        weight='heavy'
        className={cx('initials')}
        data-elm-id={`rank-buyer-${bidRank.bid_id || bidRank.rank}`}
      >
        {person ? getInitials(person) : bidRank.party_id}
      </Text>
    )
    const dynamicAvatar = (
      <InitialsAvatar
        initials={getInitials(person)}
        size='small'
        tooltipContent={
          <div>
            <LabelValue
              label='Name'
              value={`${person?.first_name ?? ''} ${person?.last_name ?? ''}`}
            />
          </div>
        }
        onClick={user ? () => goToUserManagementEditPage(user) : undefined}
      />
    )
    return viewingFrom === graph.enums.CurrentTargetAudience.Staff
      ? dynamicAvatar
      : initialsAvatar
  }

  const cellWidths: Record<string, string> = {
    'rank-rank': isMobile ? '20%' : '12%',
    'rank-buyer': isEnglishType ? '18%' : '22%',
    'english-rank-amount': '25%',
    'english-rank-type': '20%',
    'english-rank-date': '25%',
    'rank-amount': isMobile ? '35%' : isEnglishType ? '25%' : '40%',
    'rank-score': isMobile ? '35%' : '50%',
    'rank-terms': '45%',
  }

  const items = useMemo(
    () =>
      rankList.map((bidRank, index) => {
        const rank = index + 1
        const id = bidRank.bid_id || `bid-rank-${rank}`
        const isActive = expanded === id
        const isSellerBid =
          bidRank.bid?.type === graph.enums.OnlineAuctionSegmentBidType.Seller
        const showAsTied = isTied(bidRank)
        const itemAmount =
          match({
            segmentType,
            viewingFrom,
          })
            .with(
              {
                segmentType: graph.enums.OnlineAuctionSegmentType.English,
              },
              () => bidRank.bid?.bid_amount
            )
            .with(
              {
                segmentType: graph.enums.OnlineAuctionSegmentType.SealedBid,
                viewingFrom: graph.enums.CurrentTargetAudience.Buyer,
              },
              () => bidRank.bid?.terms?.offer_amount
            )
            .otherwise(() => bidRank.bid?.terms?.seller_net_offer_amount) ?? 0

        const rankAndOrdinal = getRankAndOrdinal(bidRank.max_tied_rank || rank)
        const header = () => (
          <div
            className={cx('rank-row', 'row-content', {
              'blurred-row':
                !bidRank.is_viewable_by_current_user && !showAsTied,
            })}
            data-elm-id={`rank-row-${rank}`}
          >
            <div
              className={cx('rank-cell')}
              style={{ width: cellWidths['rank-rank'] }}
            >
              <Text color='grayMedium' data-elm-id={`ranking-rank-${rank}`}>
                {showAsTied ? `T-${rankAndOrdinal}` : rankAndOrdinal}
              </Text>
            </div>
            {!isMobile && (
              <div
                data-elm-id={`rank-buyer-${rank}`}
                style={{ width: cellWidths['rank-buyer'] }}
              >
                {bidRank.belongs_to_current_user ? (
                  <div>
                    <InfoPill
                      className={cx('my-offer-pill')}
                      size='medium'
                      content='My Offer'
                      backgroundColor={ColorNames.Info}
                      foregroundColor={ColorNames.White}
                    />
                  </div>
                ) : (
                  <div className={cx('initials-container')}>
                    {isSellerBid && isEnglishType ? (
                      <Image
                        size='small'
                        src={smartSaleWhiteLogo}
                        className={cx('initials-img')}
                      />
                    ) : (
                      userAvatar(bidRank)
                    )}
                  </div>
                )}
              </div>
            )}
            <div
              className={cx('rank-cell')}
              style={{ width: cellWidths['rank-amount'] }}
              data-elm-id={`rank-amount-${rank}`}
            >
              <div>
                {isMobile && bidRank.belongs_to_current_user && (
                  <InfoPill
                    className={cx('my-offer-pill')}
                    size={isMobile ? 'small' : 'medium'}
                    content='My Offer'
                    backgroundColor={ColorNames.Info}
                    foregroundColor={ColorNames.White}
                  />
                )}
                <Money
                  color='gray'
                  weight='heavy'
                  size='large'
                  amount={itemAmount}
                />
              </div>
            </div>
            {isEnglishType ? (
              <>
                <div
                  className={cx('rank-cell')}
                  style={{ width: cellWidths['english-rank-type'] }}
                  data-elm-id={`rank-type-container-${rank}`}
                >
                  {/*   TODO define colors for bid types */}
                  {isSellerBid ? (
                    <PopupTooltip
                      on='click'
                      position='right center'
                      offset={0}
                      horizontalOffset={0}
                      verticalOffset={0}
                      trigger={
                        <Text
                          color='success'
                          data-elm-id={`rank-type-${rank}`}
                          className='u-pointer-cursor'
                        >
                          {formatEnum(bidRank.bid?.type) + ' '}
                          <Icon name='info circle' fitted />
                        </Text>
                      }
                      content={
                        <div>
                          <Text size='tiny' color='white' weight='heavy'>
                            Seller Counter bid helps buyers and sellers reach a
                            mutually agreeable price, Auction.com is authorized
                            to place a counterbid on the seller's behalf.
                            Counterbids will only bridge the gap to the reserve
                            price and stop once it is met.
                          </Text>
                        </div>
                      }
                    />
                  ) : (
                    <Text color='success' data-elm-id={`rank-type-${rank}`}>
                      {formatEnum(bidRank.bid?.type)}
                    </Text>
                  )}
                </div>
                <div
                  className={cx('rank-cell')}
                  style={{ width: cellWidths['english-rank-date'] }}
                  data-elm-id={`rank-date-container-${rank}`}
                >
                  <Text color='gray' data-elm-id={`rank-date-${rank}`}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                    {formatDate(bidRank.bid?.created_date)}
                  </Text>
                </div>
              </>
            ) : (
              <>
                <div
                  className={cx('rank-cell', 'rank-score')}
                  style={{ width: cellWidths['rank-score'] }}
                  data-elm-id={`rank-score-${rank}`}
                >
                  {showAsTied && bidRank.max_tied_rank == 1 ? (
                    <InfoPill
                      size='medium'
                      backgroundColor={ColorNames.CautionLight}
                      foregroundColor={ColorNames.Caution}
                      includeBorder
                      extraPadding={isMobile ? 'small' : 'medium'}
                      content={'Tied for 1st'}
                    />
                  ) : bidRank.overtake_delta && !acceptedOffer(bidRank) ? (
                    <InfoPill
                      size='medium'
                      backgroundColor={ColorNames.CautionLight}
                      foregroundColor={ColorNames.Caution}
                      includeBorder
                      iconName='arrow-down'
                      extraPadding={isMobile ? 'small' : 'medium'}
                      content={formatMoney(
                        viewingFromBuyer
                          ? bidRank.overtake_delta
                          : bidRank.net_offer_equal_delta
                      )}
                    />
                  ) : (
                    ''
                  )}
                </div>
                {!isMobile && (
                  <div
                    className={cx('rank-cell')}
                    style={{ width: cellWidths['rank-terms'] }}
                    data-elm-id={`rank-terms-${rank}`}
                  >
                    <BidRankTerms
                      bidRank={bidRank}
                      viewingFrom={viewingFrom}
                      requestRentBack={requestRentBack}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )

        const isCardExpandable =
          [
            graph.enums.CurrentTargetAudience.Seller,
            graph.enums.CurrentTargetAudience.Staff,
          ].includes(viewingFrom) || bidRank.belongs_to_current_user

        const content = () =>
          isEnglishType || !isCardExpandable ? (
            <NullComponent />
          ) : (
            <BidRankDetails
              bidRank={bidRank}
              listing={listing}
              slots={slots}
              viewingFrom={viewingFrom}
              segment={segment}
              embeddedContext={embeddedContext}
            />
          )

        return (
          <div id={id} key={id} style={{ position: 'relative' }}>
            <BidRankRibbon
              bidRank={bidRank}
              isEnglishType={isEnglishType}
              viewingFrom={viewingFrom}
              segment={segment}
            />
            {!isEnglishType &&
              bidRank.belongs_to_current_user &&
              actionPill && (
                <BidRankActionPill {...actionPill} isMobile={isMobile} />
              )}
            <ExpandableCard
              key={index}
              id={`bid-rank-details-${index}`}
              elmId={`bid_rank_details_${index}`}
              onClick={
                !isEnglishType && isCardExpandable
                  ? () => handleExpandOrCollapse(id)
                  : undefined
              }
              active={isActive}
              heightHints={{ collapsed: isMobile ? 100 : 130, expanded: 1058 }}
              hideHeaderWhenExpanded
              slots={{ header, content }}
              bottomMargin={16}
              borderColor={ColorNames.Info}
              borderStyle={
                bidRank.belongs_to_current_user ? 'gradient' : 'none'
              }
            />
          </div>
        )
      }),
    [
      expanded,
      handleExpandOrCollapse,
      requestRentBack,
      isEnglishType,
      isMobile,
      listing,
      rankList,
      slots,
      userAvatar,
      viewingFrom,
      segment,
    ]
  )

  const viewingFromBuyer =
    viewingFrom === graph.enums.CurrentTargetAudience.Buyer
  const conditionsTitle = viewingFromBuyer ? 'to' : 'from'

  const header = useMemo(() => {
    const headerCardHeader = (
      <>
        <div
          className={cx('rank-row', 'title', headerStyle)}
          data-elm-id='rank-row-title'
        >
          <div
            className={cx('rank-cell-title', 'first-header-column-label')}
            style={{ width: cellWidths['rank-rank'] }}
          >
            <BidRankColumnHeader
              label='Rank'
              elmId='rank-rank-title'
              isMobile={isMobile}
            />
          </div>
          {!isMobile && (
            <div
              className={cx('rank-cell-title')}
              style={{ width: cellWidths['rank-buyer'] }}
            >
              <BidRankColumnHeader
                label={isEnglishType ? 'Bidder' : 'Buyer'}
                elmId='rank-buyer-title'
                isMobile={isMobile}
              />
            </div>
          )}
          {isEnglishType ? (
            <>
              <div
                className={cx('rank-cell-title')}
                style={{ width: cellWidths['rank-amount'] }}
              >
                <BidRankColumnHeader
                  label='Bid'
                  elmId='rank-amount-title'
                  isMobile={isMobile}
                />
              </div>
              <div
                className={cx('rank-cell-title')}
                style={{ width: cellWidths['english-rank-type'] }}
              >
                <BidRankColumnHeader
                  label='Bid Information'
                  elmId='rank-type-title'
                  isMobile={isMobile}
                />
              </div>
              <div
                className={cx('rank-cell-title')}
                style={{ width: cellWidths['english-rank-date'] }}
              >
                <BidRankColumnHeader
                  label='Bid Date and Time'
                  elmId='rank-date-title'
                  isMobile={isMobile}
                />
              </div>
            </>
          ) : (
            <>
              <div
                className={cx('rank-cell-title')}
                style={{ width: cellWidths['rank-amount'] }}
              >
                {viewingFromBuyer ? (
                  <BidRankColumnHeader
                    label='Purchase Price'
                    tooltipContent='The amount a buyer agrees to pay for a property as stated in the purchase agreement.'
                    elmId='rank-amount-title'
                    isMobile={isMobile}
                  />
                ) : (
                  <BidRankColumnHeader
                    label='Net Offer Amount'
                    tooltipContent={NET_OFFER_AMOUNT_TOOLTIP}
                    elmId='rank-amount-title'
                    isMobile={isMobile}
                  />
                )}
              </div>
              <div
                className={cx('rank-cell-title')}
                style={{ width: cellWidths['rank-score'] }}
              >
                <BidRankColumnHeader
                  label={
                    isMobile
                      ? `Distance ${conditionsTitle} 1st`
                      : `Amount ${conditionsTitle} 1st Place`
                  }
                  tooltipContent={
                    viewingFromBuyer
                      ? 'Increase your purchase price by this amount to be in 1st place.'
                      : 'The difference in the net offer amount between this offer and the 1st place offer.'
                  }
                  elmId='rank-score-title'
                  isMobile={isMobile}
                />
              </div>
              {!isMobile && (
                <div
                  className={cx('rank-cell-title')}
                  style={{ width: cellWidths['rank-terms'] }}
                >
                  <BidRankColumnHeader
                    label={
                      viewingFromBuyer ? 'Closing Date' : 'Offer Conditions'
                    }
                    elmId='rank-terms-title'
                    isMobile={isMobile}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </>
    )

    return (
      <ExpandableCard
        active={false}
        id='header'
        elmId='bid_rank_header'
        slots={{
          header: () => headerCardHeader,
          content: () => <NullComponent />,
        }}
        heightHints={{ collapsed: 63, expanded: 63 }}
        hideHeaderWhenExpanded
        classNames={{
          title: cx('common-title', headerStyle),
          card: cx('card-header-title', headerStyle),
        }}
        bottomMargin={0}
        borderStyle='unset'
      />
    )
  }, [isEnglishType, isMobile, viewingFrom, segment])

  return (
    <div className={cx('rank-table')}>
      {isLoading ? (
        <Loader active inverted />
      ) : (
        <>
          <RankList itemData={[header, ...items]} />
          {!rankList.length && (
            <EmptyState title='No offers yet, but we’re looking!' />
          )}
        </>
      )}
    </div>
  )
}

export { getRankAndOrdinal }

export default BidRankList
